import { useEffect, useRef } from "react";

import { PCRS_STATUS } from "features/cdm/constants/pcrsSchemeCheck";
import { useSavePcrsSchemeCheck } from "features/cdm/hooks/useSavePcrsSchemeCheck";

// Only definitive outcomes are worth persisting; transient extension/connection
// errors carry nothing the BE can act on. Private patients are deliberately
// excluded: they have no GMS entitlement, no scheme check is performed, so there
// is nothing to persist — and posting would fire a needless (CORS/403) PCRS call.
const DEFINITIVE_STATUSES = [
  PCRS_STATUS.VALID,
  PCRS_STATUS.INVALID,
  PCRS_STATUS.NO_MEDICAL_CARD,
];

// Posts the resolved PCRS scheme-check (parsed fields + raw SOAP XML) to the BE
// once per distinct outcome, so PCRS eligibility becomes server-authoritative
// and the CDM status refetches. On the normal valid-GMS path it waits for the
// cdm-ws patient details too and posts BOTH in a single payload. Deduped by
// patient + status + scheme + validity + details-presence so re-renders don't
// re-post — but a later details arrival (e.g. after credentials are entered) is
// a distinct outcome and posts once more with the details attached.
export const useSyncPcrsSchemeCheck = ({
  patientId,
  schemeCheck,
  cdmPatientDetails,
  isCdmPatientDetailsPending,
}) => {
  const { mutate: savePcrsSchemeCheck } = useSavePcrsSchemeCheck({ patientId });
  const lastPostedRef = useRef(null);

  useEffect(() => {
    if (!patientId || !schemeCheck) return;
    if (!DEFINITIVE_STATUSES.includes(schemeCheck.status)) return;
    // Wait only while details are genuinely expected and still in flight. Once
    // they resolve (data or error), or are never expected (private / no-GMS /
    // no-credentials), this is false and we post immediately — so the post is
    // never blocked indefinitely.
    if (isCdmPatientDetailsPending) return;

    // Split the retained raw XML from the parsed detail fields (BE field names
    // still being confirmed — keep them isolated so renaming is trivial).
    let patientDetailsRawXml = null;
    let patientDetailsResult = null;
    if (cdmPatientDetails) {
      const { rawXml, ...parsed } = cdmPatientDetails;
      patientDetailsRawXml = rawXml ?? null;
      patientDetailsResult = parsed;
    }

    const signature = [
      patientId,
      schemeCheck.status,
      schemeCheck.schemeId ?? "",
      String(schemeCheck.validPatient),
      patientDetailsRawXml ? "with-details" : "no-details",
    ].join("|");
    if (lastPostedRef.current === signature) return;
    lastPostedRef.current = signature;

    savePcrsSchemeCheck({
      status: schemeCheck.status,
      validPatient: !!schemeCheck.validPatient,
      schemeCheckPerformed: !!schemeCheck.schemeCheckPerformed,
      isPrivate: schemeCheck.status === PCRS_STATUS.PRIVATE_SKIPPED,
      schemeId: schemeCheck.schemeId ?? null,
      checkedAt: new Date().toISOString(),
      invalidReason: schemeCheck.invalidReason ?? null,
      result: schemeCheck.parsedResult ?? null,
      rawXml: schemeCheck.rawXml ?? null,
      patientDetailsRawXml,
      patientDetailsResult,
    });
  }, [
    patientId,
    schemeCheck,
    cdmPatientDetails,
    isCdmPatientDetailsPending,
    savePcrsSchemeCheck,
  ]);
};
